import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Backdrop,
  Container,
  InputAdornment,
  styled
} from '@mui/material';
import {
  ArrowBack,
  Share,
  Download,
  Print,
  ContentCopy,
  CheckCircle,
  QrCode2,
  Error,
  Visibility,
  AttachMoney,
  TrendingUp
} from '@mui/icons-material';
import QRCode from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import useCurrencyConversion from '../../hooks/useCurrencyConversion';
import axios from 'axios';
import { url } from '../utils/constants';

// Styled Components
const ModernButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
  borderRadius: 12,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  color: '#FFFFFF',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-2px)',
    color: '#FFFFFF',
  },
  '& .MuiButton-endIcon': {
    color: '#FFFFFF',
  },
  transition: 'all 0.3s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #757575 0%, #616161 100%)',
  borderRadius: 12,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  color: '#FFFFFF',
  boxShadow: '0 4px 12px rgba(117, 117, 117, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #616161 0%, #424242 100%)',
    boxShadow: '0 6px 16px rgba(117, 117, 117, 0.4)',
    transform: 'translateY(-2px)',
    color: '#FFFFFF',
  },
  '& .MuiButton-startIcon': {
    color: '#FFFFFF',
  },
  transition: 'all 0.3s ease',
}));

const PixStep3 = () => {
  const { isLoggedIn, authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatCurrency } = useCurrencyConversion();

  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');
  const intervalRef = useRef(null);

  // Verificar autenticação e dados das etapas anteriores
  useEffect(() => {
    if (authLoading) return;

    if (!isLoggedIn) {
      navigate('/signin');
      return;
    }

    if (!location.state?.step1Data || !location.state?.step2Data || !location.state?.calculatedValues) {
      navigate('/pix');
      return;
    }

    const { step1Data, step2Data, calculatedValues, pixData, pixCode } = location.state;

    // Log dos dados recebidos da API
    console.log('📥 Dados recebidos no Step3:', {
      step1Data,
      step2Data,
      calculatedValues,
      pixData,
      pixCode,
      pixCopyPaste: pixData?.originalData?.pixCopyPaste,
      originalData: pixData?.originalData
    });

    // Debug específico dos valores calculados
    console.log('💰 Debug valores calculados:', {
      totalWithFee: calculatedValues?.totalWithFee,
      convertedValue: calculatedValues?.convertedValue,
      originalValue: calculatedValues?.originalValue,
      serviceFeeAmount: calculatedValues?.serviceFeeAmount,
      pairInfo: calculatedValues?.pairInfo
    });

    setQrData({
      step1Data,
      step2Data,
      calculatedValues,
      pixData,
      pixCode,
      transactionId: pixData?.id || `PIX_${Date.now()}`,
      timestamp: new Date().toISOString()
    });

    // Iniciar verificação de pagamento se temos um ID de transação
    if (pixData?.id) {
      startPaymentCheck(pixData.id);
    }
  }, [isLoggedIn, authLoading, navigate, location.state]);

  // Função para verificar status do pagamento
  const checkPaymentStatus = async (transactionId) => {
    try {
      const token = localStorage.getItem('token');

      console.log('🔍 Verificando pagamento:', transactionId);

      const response = await axios.get(
        `${url}/api/v1/verificarPagamento/${transactionId}?t=${Date.now()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      console.log('📥 Resposta da verificação:', response.data);

      const newStatus = response.data.status;

      if (newStatus === 'PAGO') {
        console.log('✅ Pagamento confirmado!');
        setPaymentConfirmed(true);
        setPaymentStatus('success');
        setPaymentMessage('Pagamento confirmado!');
        setShowPaymentDialog(true);
        stopPaymentCheck();

        // Redirecionar para relatório após 3 segundos
        setTimeout(() => {
          navigate('/relatorio-pagamento');
        }, 3000);

      } else if (newStatus === 'Expirado') {
        console.log('⏰ Pagamento expirado');
        setPaymentStatus('error');
        setPaymentMessage('Pagamento expirado');
        setShowPaymentDialog(true);
        stopPaymentCheck();
      } else {
        console.log('⏳ Pagamento pendente, verificando novamente em 5s...');
      }

      return newStatus;
    } catch (error) {
      console.error('❌ Erro ao verificar status do pagamento:', error);
      return null;
    }
  };

  // Iniciar verificação automática
  const startPaymentCheck = (transactionId) => {
    if (intervalRef.current) return; // Já está verificando

    console.log('🚀 Iniciando verificação automática de pagamento');

    // Verificar imediatamente
    checkPaymentStatus(transactionId);

    // Verificar a cada 5 segundos
    intervalRef.current = setInterval(() => {
      checkPaymentStatus(transactionId);
    }, 5000);
  };

  // Parar verificação
  const stopPaymentCheck = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      stopPaymentCheck();
    };
  }, []);

  const generatePixCode = () => {
    if (!qrData) return '';

    // Usar o código PIX real da API (pixCopyPaste é o campo correto)
    const pixCode = qrData.pixData?.originalData?.pixCopyPaste || qrData.pixData?.pixCopyPaste || qrData.pixCode;

    console.log('🔍 Debug generatePixCode:', {
      qrData: qrData,
      pixData: qrData?.pixData,
      originalData: qrData?.pixData?.originalData,
      pixCopyPaste: qrData?.pixData?.originalData?.pixCopyPaste,
      pixCode: pixCode
    });

    if (pixCode) {
      console.log('🎯 Usando código PIX da API:', pixCode);
      return pixCode;
    }

    // Fallback: gerar código PIX mockado
    console.warn('⚠️ Usando código PIX mockado');
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 8);
    return `00020126580014br.gov.bcb.pix0136${timestamp}5204000053039865802BR5909Pixtick6009SAOPAULO62070503***6304${randomId}`;
  };

  const handleCopyCode = async () => {
    try {
      const pixCode = generatePixCode();
      await navigator.clipboard.writeText(pixCode);
      setCopied(true);
      toast.success('Código PIX copiado para a área de transferência!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar código:', error);
      toast.error('Erro ao copiar código PIX');
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('pix-qr-code');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `pix-qr-${qrData.transactionId}.png`;
      link.href = canvas.toDataURL();
      link.click();
      toast.success('QR Code baixado com sucesso!');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    setShowShareDialog(true);
  };

  const handleBack = () => {
    navigate('/pix/step2', {
      state: {
        formData: qrData.step1Data
      }
    });
  };

  const handleNewTransaction = () => {
    navigate('/pix');
  };

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!qrData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Box textAlign="center">
          <CircularProgress size={60} sx={{ mb: 2, color: '#1976D2' }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Carregando QR Code...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aguarde enquanto geramos o código PIX
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#FFFFFA',
      padding: 1.5,
      pt: { xs: '8px', md: '30px' },
      pb: { xs: '30px', md: '15px' }
    }}>
      <Box maxWidth="lg" mx="auto" mt={{ xs: 6, sm: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ mb: 2, background: '#1976D2', color: 'white' }}>
          <Box p={{ xs: 1.5, sm: 2 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 1.5, sm: 0 }}
            >
              <Box textAlign={{ xs: 'center', sm: 'left' }}>
                <Typography variant="h6" sx={{
                  opacity: 0.9,
                  fontSize: { xs: '1rem', md: '1.2rem' }
                }}>
                  QR Code PIX
                </Typography>
              </Box>
              <Box display="flex" gap={1} width={{ xs: '100%', sm: 'auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    flex: { xs: 1, sm: 'none' }
                  }}
                >
                  VOLVER
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleNewTransaction}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    flex: { xs: 1, sm: 'none' }
                  }}
                >
                  NOVO
                </Button>
                {paymentConfirmed && (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/relatorio-pagamento')}
                    sx={{
                      backgroundColor: '#5B8C5A',
                      color: 'white',
                      flex: { xs: 1, sm: 'none' },
                      '&:hover': {
                        backgroundColor: '#4a7c4a'
                      }
                    }}
                  >
                    VER RELATÓRIO
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Stepper */}
        <Box mb={2}>
          <Stepper activeStep={2} alternativeLabel>
            <Step>
              <StepLabel sx={{ fontSize: '0.8rem' }}>Valor</StepLabel>
            </Step>
            <Step>
              <StepLabel sx={{ fontSize: '0.8rem' }}>Cliente</StepLabel>
            </Step>
            <Step>
              <StepLabel sx={{ fontSize: '0.8rem' }}>QR Code</StepLabel>
            </Step>
          </Stepper>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {/* QR Code Principal */}
          <Grid item xs={12} md={8}>
            <Card elevation={3}>
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                  Total a cobrar
                </Typography>

                {/* Valores - Total em BRL (principal) e Moeda original (secundário) */}
                <Box mb={2}>
                  <Typography variant="h4" color="primary" fontWeight="bold" sx={{ fontSize: '1.8rem' }}>
                    R$ {(qrData.calculatedValues?.totalWithFee || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.9rem' }}>
                    {formatCurrency(qrData.calculatedValues?.originalValue || 0, qrData.calculatedValues?.pairInfo?.from || 'ARS')}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    mb: 2,
                    border: '2px dashed #dee2e6',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <QRCode
                    id="pix-qr-code"
                    value={(() => {
                      const code = generatePixCode();
                      console.log('🔍 QR Code sendo gerado com valor:', code);
                      console.log('🔍 Tamanho do código:', code.length);
                      return code;
                    })()}
                    size={220}
                    level="H"
                    includeMargin={true}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
                  Escaneie o QR Code com seu aplicativo de pagamento
                </Typography>

                {!paymentConfirmed && (
                  <Box sx={{
                    mb: 2,
                    p: 1.5,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    border: '1px solid #e9ecef',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      Aguardando confirmação do pagamento...
                    </Typography>
                  </Box>
                )}

                {/* Status do Pagamento - Visual Limpo */}
                {paymentConfirmed && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="success" sx={{ mb: 1 }}>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}>
                        <CheckCircle />
                        Pagamento Confirmado!
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {/* Botões de Ação */}
                <Grid container spacing={1} justifyContent="center">
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                      onClick={handleCopyCode}
                      color={copied ? 'success' : 'primary'}
                    >
                      {copied ? 'Copiado!' : 'Copiar Código'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleDownloadQR}
                    >
                      Baixar QR
                    </Button>
                  </Grid>
                  {/* <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<Print />}
                      onClick={handlePrint}
                    >
                      Imprimir
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<Share />}
                      onClick={handleShare}
                    >
                      Compartilhar
                    </Button>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Box>

      {/* Dialog de Compartilhamento */}
      <Dialog open={showShareDialog} onClose={() => setShowShareDialog(false)}>
        <DialogTitle>Compartilhar PIX</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Escolha como deseja compartilhar o QR Code:
          </Typography>
          <Box mt={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => {
                handleCopyCode();
                setShowShareDialog(false);
              }}
              sx={{ mb: 1 }}
            >
              Copiar Link
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download />}
              onClick={() => {
                handleDownloadQR();
                setShowShareDialog(false);
              }}
            >
              Baixar Imagem
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShareDialog(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Pagamento */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Fade in={showPaymentDialog} timeout={500}>
            <Box>
              {/* Ícone de Status */}
              <Box sx={{ mb: 3 }}>
                {paymentStatus === 'success' ? (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: '#F44336',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      boxShadow: '0 4px 20px rgba(244, 67, 54, 0.3)'
                    }}
                  >
                    <Error sx={{ fontSize: 50, color: 'white' }} />
                  </Box>
                )}
              </Box>

              {/* Mensagem */}
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: paymentStatus === 'success' ? '#4CAF50' : '#F44336',
                  fontWeight: 'bold'
                }}
              >
                {paymentMessage}
              </Typography>

              {/* Contador para redirecionamento (apenas em caso de sucesso) */}
              {paymentStatus === 'success' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Redirecionando para o relatório em 3 segundos...
                </Typography>
              )}
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          {paymentStatus === 'error' && (
            <Button
              variant="contained"
              onClick={() => setShowPaymentDialog(false)}
              sx={{
                backgroundColor: '#F44336',
                '&:hover': { backgroundColor: '#D32F2F' }
              }}
            >
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PixStep3;
